export type PdfDeliveryStrategy = 'share' | 'download' | 'blocked';

export type PdfDeliveryStatus = 'delivered' | 'cancelled' | 'blocked' | 'failed';

export interface PdfDeliveryEnvironment {
  userAgent: string;
  canShareFiles: boolean;
}

export interface PdfDeliveryArtifact {
  blob: Blob;
  file: File | null;
  fileName: string;
}

export interface PdfDeliveryResult {
  strategy: PdfDeliveryStrategy;
  status: PdfDeliveryStatus;
}

export interface PdfDeliveryOptions {
  environment?: PdfDeliveryEnvironment;
  share?: (file: File) => Promise<void>;
  download?: (blob: Blob, fileName: string) => void;
}

export const FACEBOOK_INSTAGRAM_PDF_MESSAGE =
  'Facebook/Instagram może blokować zapis PDF. Otwórz tę stronę w zewnętrznej przeglądarce — Chrome lub Safari — i pobierz ponownie.';
export const PDF_SHARE_CANCELLED_MESSAGE =
  'Udostępnianie PDF anulowane. Dokument został utworzony, ale nie został dostarczony.';
export const PDF_DELIVERY_FAILED_MESSAGE =
  'Nie udało się dostarczyć PDF. Spróbuj ponownie.';

const FACEBOOK_INSTAGRAM_WEBVIEW_PATTERN = /\b(?:FBAN|FBAV|FB_IAB|FB4A|FBIOS|Facebook|Instagram)\b/i;

// The browser needs the object URL to stay reachable until it has actually
// started writing the file. Revoking on the next tick is enough on desktop but
// aborts the download in mobile WebViews, so the URL is released only after a
// delay that is longer than any realistic download hand-off.
const PDF_OBJECT_URL_LIFETIME_MS = 60_000;

export function isFacebookOrInstagramWebView(userAgent: string): boolean {
  return FACEBOOK_INSTAGRAM_WEBVIEW_PATTERN.test(userAgent);
}

export function resolvePdfDeliveryStrategy(
  environment: PdfDeliveryEnvironment
): PdfDeliveryStrategy {
  if (environment.canShareFiles) return 'share';
  if (isFacebookOrInstagramWebView(environment.userAgent)) return 'blocked';
  return 'download';
}

export function detectPdfDeliveryEnvironment(file: File | null): PdfDeliveryEnvironment {
  if (typeof navigator === 'undefined') {
    return { userAgent: '', canShareFiles: false };
  }

  let canShareFiles = false;
  if (file && typeof navigator.share === 'function' && typeof navigator.canShare === 'function') {
    try {
      canShareFiles = navigator.canShare({ files: [file] });
    } catch {
      canShareFiles = false;
    }
  }

  return { userAgent: navigator.userAgent, canShareFiles };
}

export async function deliverPdfArtifact(
  artifact: PdfDeliveryArtifact,
  options: PdfDeliveryOptions = {}
): Promise<PdfDeliveryResult> {
  const environment = options.environment ?? detectPdfDeliveryEnvironment(artifact.file);
  const strategy = resolvePdfDeliveryStrategy(environment);

  if (strategy === 'blocked') {
    return { strategy, status: 'blocked' };
  }

  if (strategy === 'share') {
    if (!artifact.file) return { strategy, status: 'failed' };

    try {
      await (options.share ?? sharePdfFile)(artifact.file);
      return { strategy, status: 'delivered' };
    } catch (error) {
      if (isPdfShareCancellation(error)) {
        return { strategy, status: 'cancelled' };
      }

      return {
        strategy,
        status: isFacebookOrInstagramWebView(environment.userAgent) ? 'blocked' : 'failed',
      };
    }
  }

  try {
    (options.download ?? downloadPdfBlob)(artifact.blob, artifact.fileName);
    return { strategy, status: 'delivered' };
  } catch {
    return { strategy, status: 'failed' };
  }
}

export function isPdfShareCancellation(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

async function sharePdfFile(file: File): Promise<void> {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
    throw new Error('PDF_SHARE_UNAVAILABLE');
  }

  await navigator.share({ files: [file] });
}

function downloadPdfBlob(blob: Blob, fileName: string): void {
  if (typeof document === 'undefined' || !document.body || typeof URL.createObjectURL !== 'function') {
    throw new Error('PDF_DOWNLOAD_UNAVAILABLE');
  }

  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();

  globalThis.setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, PDF_OBJECT_URL_LIFETIME_MS);
}
